import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { DatasetQuestion, MatrixConfig } from '../../lib/api';

interface QuestionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: Partial<DatasetQuestion>;
  onChange: (question: Partial<DatasetQuestion>) => void;
  onSave: () => void;
  isEditing: boolean;
}

interface MatrixConfigEditorProps {
  config: MatrixConfig;
  isScored?: boolean;
  onChange: (config: MatrixConfig) => void;
}

function MatrixConfigEditor({ config, isScored, onChange }: MatrixConfigEditorProps) {
  const [newRowLabel, setNewRowLabel] = useState('');
  const [newColLabel, setNewColLabel] = useState('');

  const handleAddRow = () => {
    if (!newRowLabel.trim()) return;
    const newRow = {
      id: `row_${Date.now()}`,
      label: newRowLabel.trim(),
    };
    onChange({
      ...config,
      rows: [...(config.rows || []), newRow],
    });
    setNewRowLabel('');
  };

  const handleAddColumn = () => {
    if (!newColLabel.trim()) return;
    const newCol = {
      id: `col_${Date.now()}`,
      label: newColLabel.trim(),
      score: isScored ? 0 : undefined,
    };
    onChange({
      ...config,
      columns: [...(config.columns || []), newCol],
    });
    setNewColLabel('');
  };

  const handleRemoveRow = (index: number) => {
    onChange({
      ...config,
      rows: config.rows.filter((_, i) => i !== index),
    });
  };

  const handleRemoveColumn = (index: number) => {
    onChange({
      ...config,
      columns: config.columns.filter((_, i) => i !== index),
    });
  };

  const handleUpdateRowLabel = (index: number, label: string) => {
    const updated = config.rows.map((row, i) =>
      i === index ? { ...row, label } : row
    );
    onChange({ ...config, rows: updated });
  };

  const handleUpdateColumn = (index: number, updates: Partial<{ label: string; score: number }>) => {
    const updated = config.columns.map((col, i) =>
      i === index ? { ...col, ...updates } : col
    );
    onChange({ ...config, columns: updated });
  };

  return (
    <div className="space-y-4">
      <Label>Matrix Configuration</Label>
      <p className="text-sm text-muted-foreground">
        Define rows (items to evaluate) and columns (rating options)
      </p>

      {/* Rows */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Rows (Items)</Label>
        {config.rows?.length > 0 && (
          <div className="space-y-2">
            {config.rows.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={row.label}
                  onChange={(e) => handleUpdateRowLabel(index, e.target.value)}
                  placeholder="Row label"
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveRow(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input
            value={newRowLabel}
            onChange={(e) => setNewRowLabel(e.target.value)}
            placeholder="Add new row..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddRow();
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddRow}
            disabled={!newRowLabel.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Columns */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Columns (Rating Options)</Label>
        {config.columns?.length > 0 && (
          <div className="space-y-2">
            {config.columns.map((col, index) => (
              <div key={col.id} className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Input
                  value={col.label}
                  onChange={(e) => handleUpdateColumn(index, { label: e.target.value })}
                  placeholder="Column label"
                  className="flex-1"
                />
                {isScored && (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min="0"
                      value={col.score ?? 0}
                      onChange={(e) => handleUpdateColumn(index, { score: Number(e.target.value) })}
                      className="w-20"
                    />
                    <span className="text-sm text-muted-foreground">pts</span>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveColumn(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Input
            value={newColLabel}
            onChange={(e) => setNewColLabel(e.target.value)}
            placeholder="Add new column..."
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddColumn();
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddColumn}
            disabled={!newColLabel.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Allow Multiple Selection */}
      <div className="flex items-center justify-between p-3 border rounded-lg">
        <div>
          <Label>Allow Multiple Selection</Label>
          <p className="text-sm text-muted-foreground">
            Allow selecting multiple columns per row
          </p>
        </div>
        <Switch
          checked={config.allowMultiple ?? false}
          onCheckedChange={(checked) => onChange({ ...config, allowMultiple: checked })}
        />
      </div>

      {/* Preview */}
      {config.rows?.length > 0 && config.columns?.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Preview</Label>
          <div className="border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-2 text-left font-medium"></th>
                  {config.columns.map((col) => (
                    <th key={col.id} className="p-2 text-center font-medium">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {config.rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-0">
                    <td className="p-2 font-medium">{row.label}</td>
                    {config.columns.map((col) => (
                      <td key={col.id} className="p-2 text-center">
                        <input
                          type={config.allowMultiple ? 'checkbox' : 'radio'}
                          name={`preview_${row.id}`}
                          disabled
                          className="h-4 w-4"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text', description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', description: 'Multi-line text area' },
  { value: 'number', label: 'Number', description: 'Numeric input' },
  { value: 'date', label: 'Date', description: 'Date picker' },
  { value: 'yes_no', label: 'Yes/No', description: 'Binary choice' },
  { value: 'single_choice', label: 'Single Choice', description: 'Radio buttons - one answer' },
  { value: 'multiple_choice', label: 'Multiple Choice', description: 'Checkboxes - multiple answers' },
  { value: 'file_upload', label: 'File Upload', description: 'Document attachment' },
  { value: 'rating', label: 'Rating', description: 'Star or numeric rating' },
  { value: 'matrix', label: 'Matrix', description: 'Grid with rows and columns' },
  { value: 'email', label: 'Email', description: 'Email address input' },
  { value: 'phone', label: 'Phone', description: 'Phone number input' },
  { value: 'url', label: 'URL', description: 'Website URL input' },
  { value: 'currency', label: 'Currency', description: 'Money amount input' },
  { value: 'percentage', label: 'Percentage', description: 'Percentage input' },
];

const CHOICE_TYPES = ['single_choice', 'multiple_choice'];

export default function QuestionEditor({
  open,
  onOpenChange,
  question,
  onChange,
  onSave,
  isEditing,
}: QuestionEditorProps) {
  const [newChoiceLabel, setNewChoiceLabel] = useState('');

  const hasChoices = CHOICE_TYPES.includes(question.question_type || '');
  const choices = question.choices || [];

  const handleAddChoice = () => {
    if (!newChoiceLabel.trim()) return;

    const newChoice = {
      value: newChoiceLabel.toLowerCase().replace(/\s+/g, '_'),
      label: newChoiceLabel,
      score: question.is_scored ? 0 : undefined,
    };

    onChange({
      ...question,
      choices: [...choices, newChoice],
    });
    setNewChoiceLabel('');
  };

  const handleRemoveChoice = (index: number) => {
    const updated = choices.filter((_, i) => i !== index);
    onChange({ ...question, choices: updated });
  };

  const handleUpdateChoice = (index: number, field: 'label' | 'score', value: string | number) => {
    const updated = choices.map((choice, i) => {
      if (i === index) {
        if (field === 'label') {
          return {
            ...choice,
            label: value as string,
            value: (value as string).toLowerCase().replace(/\s+/g, '_'),
          };
        }
        // Ensure score is stored as number
        return { ...choice, score: Number(value) };
      }
      return choice;
    });
    onChange({ ...question, choices: updated });
  };

  const handleTypeChange = (type: string) => {
    const updates: Partial<DatasetQuestion> = { question_type: type as DatasetQuestion['question_type'] };

    // Reset choices if switching away from choice types
    if (!CHOICE_TYPES.includes(type)) {
      updates.choices = [];
    }

    // Set default choices for yes_no
    if (type === 'yes_no') {
      updates.choices = [
        { value: 'yes', label: 'Yes', score: question.is_scored ? 1 : undefined },
        { value: 'no', label: 'No', score: question.is_scored ? 0 : undefined },
      ];
    }

    onChange({ ...question, ...updates });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Question' : 'Add Question'}</DialogTitle>
          <DialogDescription>
            Configure the question settings and options
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Question Text */}
          <div className="space-y-2">
            <Label htmlFor="questionText">Question *</Label>
            <Textarea
              id="questionText"
              value={question.question_text || ''}
              onChange={(e) => onChange({ ...question, question_text: e.target.value })}
              placeholder="Enter your question here..."
              rows={2}
            />
          </div>

          {/* Help Text */}
          <div className="space-y-2">
            <Label htmlFor="helpText">Help Text</Label>
            <Input
              id="helpText"
              value={question.help_text || ''}
              onChange={(e) => onChange({ ...question, help_text: e.target.value })}
              placeholder="Additional instructions or guidance for the respondent"
            />
          </div>

          {/* Question Type */}
          <div className="space-y-2">
            <Label>Question Type *</Label>
            <Select
              value={question.question_type}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select question type" />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <span className="font-medium">{type.label}</span>
                      <span className="text-muted-foreground ml-2 text-sm">
                        - {type.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Required & Scored */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label>Required</Label>
                <p className="text-sm text-muted-foreground">
                  Must be answered
                </p>
              </div>
              <Switch
                checked={question.is_required ?? true}
                onCheckedChange={(checked) => onChange({ ...question, is_required: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label>Scored</Label>
                <p className="text-sm text-muted-foreground">
                  Include in scoring
                </p>
              </div>
              <Switch
                checked={question.is_scored ?? false}
                onCheckedChange={(checked) => {
                  const updates: Partial<DatasetQuestion> = { is_scored: checked };
                  if (checked && !question.max_score) {
                    updates.max_score = 10;
                  }
                  onChange({ ...question, ...updates });
                }}
              />
            </div>
          </div>

          {/* Max Score */}
          {question.is_scored && (
            <div className="space-y-2">
              <Label htmlFor="maxScore">Maximum Score</Label>
              <Input
                id="maxScore"
                type="number"
                min="1"
                value={question.max_score || 10}
                onChange={(e) => onChange({ ...question, max_score: Number(e.target.value) })}
              />
            </div>
          )}

          {/* Choices for single/multiple choice */}
          {hasChoices && (
            <div className="space-y-4">
              <Label>Answer Choices</Label>

              {choices.length > 0 && (
                <div className="space-y-2">
                  {choices.map((choice, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <Input
                        value={choice.label}
                        onChange={(e) => handleUpdateChoice(index, 'label', e.target.value)}
                        placeholder="Choice label"
                        className="flex-1"
                      />
                      {question.is_scored && (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            value={choice.score ?? 0}
                            onChange={(e) => handleUpdateChoice(index, 'score', Number(e.target.value))}
                            className="w-20"
                          />
                          <span className="text-sm text-muted-foreground">pts</span>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveChoice(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Input
                  value={newChoiceLabel}
                  onChange={(e) => setNewChoiceLabel(e.target.value)}
                  placeholder="Add new choice..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddChoice();
                    }
                  }}
                />
                <Button
                  variant="outline"
                  onClick={handleAddChoice}
                  disabled={!newChoiceLabel.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Yes/No choices (display only) */}
          {question.question_type === 'yes_no' && question.is_scored && (
            <div className="space-y-2">
              <Label>Yes/No Scores</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 p-3 border rounded-lg">
                  <Badge variant="outline">Yes</Badge>
                  <Input
                    type="number"
                    min="0"
                    value={choices.find(c => c.value === 'yes')?.score ?? 1}
                    onChange={(e) => {
                      const updated = choices.map(c =>
                        c.value === 'yes' ? { ...c, score: Number(e.target.value) } : c
                      );
                      onChange({ ...question, choices: updated });
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">pts</span>
                </div>
                <div className="flex items-center gap-2 p-3 border rounded-lg">
                  <Badge variant="outline">No</Badge>
                  <Input
                    type="number"
                    min="0"
                    value={choices.find(c => c.value === 'no')?.score ?? 0}
                    onChange={(e) => {
                      const updated = choices.map(c =>
                        c.value === 'no' ? { ...c, score: Number(e.target.value) } : c
                      );
                      onChange({ ...question, choices: updated });
                    }}
                    className="w-20"
                  />
                  <span className="text-sm text-muted-foreground">pts</span>
                </div>
              </div>
            </div>
          )}

          {/* Rating configuration */}
          {question.question_type === 'rating' && (
            <div className="space-y-2">
              <Label>Rating Configuration</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minRating">Minimum</Label>
                  <Input
                    id="minRating"
                    type="number"
                    min="0"
                    value={Number(question.validation_rules?.['min'] ?? 1)}
                    onChange={(e) => onChange({
                      ...question,
                      validation_rules: {
                        ...question.validation_rules,
                        min: Number(e.target.value),
                      },
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxRating">Maximum</Label>
                  <Input
                    id="maxRating"
                    type="number"
                    max="10"
                    value={Number(question.validation_rules?.['max'] ?? 5)}
                    onChange={(e) => onChange({
                      ...question,
                      validation_rules: {
                        ...question.validation_rules,
                        max: Number(e.target.value),
                      },
                    })}
                  />
                </div>
              </div>
            </div>
          )}

          {/* File Upload configuration */}
          {question.question_type === 'file_upload' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="maxFiles">Maximum Files</Label>
                <Input
                  id="maxFiles"
                  type="number"
                  min="1"
                  max="10"
                  value={question.max_attachments ?? 1}
                  onChange={(e) => onChange({
                    ...question,
                    max_attachments: Number(e.target.value),
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Allowed File Types</Label>
                <div className="flex flex-wrap gap-2">
                  {['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PNG', 'JPG'].map((type) => {
                    const allowed = question.allowed_file_types || ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg'];
                    const isSelected = allowed.includes(type.toLowerCase());
                    return (
                      <Badge
                        key={type}
                        variant={isSelected ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const updated = isSelected
                            ? allowed.filter(t => t !== type.toLowerCase())
                            : [...allowed, type.toLowerCase()];
                          onChange({ ...question, allowed_file_types: updated });
                        }}
                      >
                        {type}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Matrix configuration */}
          {question.question_type === 'matrix' && (
            <MatrixConfigEditor
              config={question.matrix_config || { rows: [], columns: [] }}
              isScored={question.is_scored}
              onChange={(config) => onChange({ ...question, matrix_config: config })}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            {isEditing ? 'Save Changes' : 'Add Question'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
